import { TOOLTIP_STYLE } from "@/lib/chartTheme";
import { isWonStatus } from "@/data/quoteTypes";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { fetchProfileDisplayNames } from "@/lib/profileLookupCache";
import { isUuidLike } from "@/lib/profileDisplayName";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Target, BarChart3, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

export interface SalesIntelligenceQuote {
  createdBy?: string;
  status?: string;
  totalEstimate?: number;
  leadSource?: string;
}

const CHART_COLORS = {
  primary: "hsl(var(--chart-1))",
  success: "hsl(var(--chart-2))",
  accent: "hsl(var(--chart-3))",
};
const LIST_PAGE_SIZE = 10;

function ListPagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (next: number) => void;
}) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-6 px-2"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </Button>
      <span className="text-[10px] text-muted-foreground">{page}/{totalPages}</span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-6 px-2"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function isWonQuoteStatus(status: SalesIntelligenceQuote["status"]): boolean {
  return isWonStatus((status ?? "") as Parameters<typeof isWonStatus>[0]);
}

export function SalesIntelligenceReport({ quotes: scopedQuotes }: { quotes?: SalesIntelligenceQuote[] } = {}) {
  const appData = useAppData();
  const quotes = scopedQuotes ?? appData.quotes;
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());
  const [repPage, setRepPage] = useState(1);
  const [leadPage, setLeadPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          quotes.map((q) => String(q.createdBy ?? "").trim()).filter((v) => isUuidLike(v)),
        ),
      );

      if (repIds.length === 0) {
        if (!cancelled) setRepNameById(new Map());
        return;
      }

      const nameMap = await fetchProfileDisplayNames(repIds);
      if (!cancelled) setRepNameById(nameMap);
    };

    loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [quotes]);

  const getRepDisplayName = useCallback((rawRep: string) => {
    if (!rawRep || rawRep === "Unknown") return rawRep;
    if (isUuidLike(rawRep)) {
      return repNameById.get(rawRep) ?? rawRep;
    }
    return rawRep;
  }, [repNameById]);

  const paginate = <T,>(items: T[], page: number) => {
    const totalPages = Math.max(1, Math.ceil(items.length / LIST_PAGE_SIZE));
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const start = (currentPage - 1) * LIST_PAGE_SIZE;
    return { page: currentPage, totalPages, items: items.slice(start, start + LIST_PAGE_SIZE) };
  };

  /* ── Sales Rep Performance (quote-based) ── */
  const repPerf = useMemo(() => {
    const reps = new Map<string, { created: number; won: number; revenue: number; closed: number }>();
    quotes.forEach((q) => {
      const repKey = q.createdBy || "Unknown";
      const existing = reps.get(repKey) || { created: 0, won: 0, revenue: 0, closed: 0 };
      existing.created++;
      if (isWonQuoteStatus(q.status) || q.status === "Closed Lost") existing.closed++;
      if (isWonQuoteStatus(q.status)) {
        existing.won++;
        existing.revenue += q.totalEstimate || 0;
      }
      reps.set(repKey, existing);
    });
    return [...reps.entries()]
      .map(([rep, data]) => ({
        rep,
        repDisplay: getRepDisplayName(rep),
        ...data,
        closeRate: data.closed > 0 ? Math.round((data.won / data.closed) * 100) : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [quotes, getRepDisplayName]);

  /* ── Lead Source Performance (quote-based) ── */
  const leadPerf = useMemo(() => {
    const sources = new Map<string, { quotes: number; won: number; revenue: number; closed: number }>();
    quotes.forEach((q) => {
      const src = q.leadSource || "Direct / Unknown";
      const existing = sources.get(src) || { quotes: 0, won: 0, revenue: 0, closed: 0 };
      existing.quotes++;
      if (isWonQuoteStatus(q.status) || q.status === "Closed Lost") existing.closed++;
      if (isWonQuoteStatus(q.status)) {
        existing.won++;
        existing.revenue += q.totalEstimate || 0;
      }
      sources.set(src, existing);
    });
    return [...sources.entries()]
      .map(([source, data]) => ({
        source,
        ...data,
        closeRate: data.closed > 0 ? Math.round((data.won / data.closed) * 100) : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [quotes]);

  const repChartData = repPerf.map((r) => ({
    name: r.repDisplay.split(" ")[0],
    Quotes: r.created,
    Won: r.won,
  }));

  const leadChartData = leadPerf.map((l) => ({
    name: l.source.length > 12 ? l.source.slice(0, 10) + "…" : l.source,
    Revenue: l.revenue,
    Quotes: l.quotes,
  }));

  return (
    <div className="space-y-4">
      {/* ── Rep Performance ── */}
      <Card>
        <CardHeader className="pb-2 px-4 pt-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-semibold">Sales Rep Performance (Quotes)</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {repChartData.length > 1 && (
            <div className="h-48 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={repChartData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="Quotes" fill={CHART_COLORS.primary} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Won" fill={CHART_COLORS.success} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
            <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-t bg-muted/40">
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Rep</th>
                <th className="text-center py-2 px-2 font-medium text-muted-foreground">Created</th>
                <th className="text-center py-2 px-2 font-medium text-muted-foreground">Won</th>
                <th className="text-center py-2 px-2 font-medium text-muted-foreground">Close Rate</th>
                <th className="text-right py-2 px-3 font-medium text-muted-foreground">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {paginate(repPerf, repPage).items.map((r) => (
                <tr key={r.rep} className="border-b last:border-0 hover:bg-muted/30">
                   <td className="py-1.5 px-3 font-medium">{r.repDisplay}</td>
                  <td className="py-1.5 px-2 text-center">{r.created}</td>
                  <td className="py-1.5 px-2 text-center font-bold text-success">{r.won}</td>
                  <td className="py-1.5 px-2 text-center">
                    <span className={`font-bold ${r.closeRate >= 50 ? "text-success" : r.closeRate >= 25 ? "text-warning" : "text-muted-foreground"}`}>
                      {r.closeRate}%
                    </span>
                  </td>
                  <td className="py-1.5 px-3 text-right font-medium">${r.revenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ListPagination
            page={paginate(repPerf, repPage).page}
            totalPages={paginate(repPerf, repPage).totalPages}
            onChange={setRepPage}
          />
        </CardContent>
      </Card>

      {/* ── Lead Source Performance ── */}
      <Card>
        <CardHeader className="pb-2 px-4 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <CardTitle className="text-sm font-semibold">Lead Source Performance (Quotes)</CardTitle>
            </div>
            <Link to="/marketing-attribution" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              Marketing <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {leadChartData.length > 1 && (
            <div className="h-48 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leadChartData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value: number) => `$${value.toLocaleString()}`} />
                  <Bar dataKey="Revenue" fill={CHART_COLORS.accent} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
            <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-t bg-muted/40">
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Source</th>
                <th className="text-center py-2 px-2 font-medium text-muted-foreground">Quotes</th>
                <th className="text-center py-2 px-2 font-medium text-muted-foreground">Won</th>
                <th className="text-center py-2 px-2 font-medium text-muted-foreground">Close Rate</th>
                <th className="text-right py-2 px-3 font-medium text-muted-foreground">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {paginate(leadPerf, leadPage).items.map((l) => (
                <tr key={l.source} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-1.5 px-3 font-medium">{l.source}</td>
                  <td className="py-1.5 px-2 text-center">{l.quotes}</td>
                  <td className="py-1.5 px-2 text-center font-bold text-success">{l.won}</td>
                  <td className="py-1.5 px-2 text-center">
                    <span className={`font-bold ${l.closeRate >= 50 ? "text-success" : l.closeRate >= 25 ? "text-warning" : "text-muted-foreground"}`}>
                      {l.closeRate}%
                    </span>
                  </td>
                  <td className="py-1.5 px-3 text-right font-medium">${l.revenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ListPagination
            page={paginate(leadPerf, leadPage).page}
            totalPages={paginate(leadPerf, leadPage).totalPages}
            onChange={setLeadPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}
